import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FF, redirectToLogout, redirectToV2Logout } from './utils';
import UserService from '../services/user.service';
import { setIMUser, setUser, setFeatures, fetchCadenceAccounts, setDeviceId } from '../redux/actions/appActions';
import invoiceManagerService from '../services/invoice-manager.service';
import { featureService } from '../services/feature.service';
import rtsproUserService from '../services/rtsproUser.service';
import type { User } from 'interfaces/user';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';
import { useAuth0 } from '@auth0/auth0-react';
import { addInterceptorsToServices } from './requestInterceptors';
import { RootState } from 'bootstrap';
const Config = require('Config');

type Errors = 'user' | undefined;

// TODO: Find a functional IP provider.
// geolocation-db.com/json is no longer functional
//
// async function attemptSetClientIp(dispatch: Function) {
//   try {
//     const clientIpResponse = await clientIpService.getClientIp();
//     dispatch(setClientIp(clientIpResponse.IPv4));
//   } catch (error) {
//     console.error(`Error while fetching Client Ip: ${error}`);
//   }
// }

async function attemptSetIMUser(user: User, email: string, dispatch: Function) {
  try {
    const isFinancial = user?.permissions
      ?.filter((p) => p.status === 'active')
      .map((p) => p.name)
      .includes('invoicemanager.User');
    if (isFinancial) {
      const imUser = await invoiceManagerService.getIMUser(email);
      const accountNumbers = imUser.accounts.map((account) => {
        return account.reference_id;
      });
      if (imUser.active_account) {
        accountNumbers.push(imUser.active_account);
      }
      dispatch(fetchCadenceAccounts(accountNumbers, imUser));
      dispatch(setIMUser(imUser));
    }
  } catch (error) {
    console.error(error);
  }
}

export function useLoadData() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Errors>();
  const {
    isAuthenticated: isAuthenticatedAuth0,
    getIdTokenClaims,
    getAccessTokenSilently,
    isLoading,
    logout,
    loginWithRedirect,
  } = useAuth0();

  const features = useSelector<RootState, string[]>((state) => state.app.features);
  const user = useSelector<RootState, string[]>((state) => state.app.user);
  let isAuthenticated = false;
  try {
    isAuthenticated = !!LocalAuth.checkSession();
  } catch {
    isAuthenticated = isAuthenticatedAuth0 || false;
  }

  const loadData = useCallback(async () => {
    setLoading(true);

    let deviceId = localStorage.getItem('device_id');
    if (deviceId) {
      dispatch(setDeviceId(deviceId));
    }
    let email = LocalAuth.getEmail();
    await addInterceptorsToServices(getAccessTokenSilently, loginWithRedirect);
    if (isAuthenticatedAuth0) {
      const claims = await getIdTokenClaims();
      email = claims?.upn || claims?.email;
    }
    const [userResult, featureResult, rtsUser] = await Promise.allSettled([
      UserService.getUser(email, true),
      featureService.getFeatures(email),
      rtsproUserService.getUserByEmail(email),
    ]);

    const features = featureResult.status === 'fulfilled' ? featureResult.value : [];
    let user: User;

    if (isAuthenticatedAuth0 && !features.includes(FF.AUTH0)) {
      await logout({ logoutParams: { returnTo: `${Config.rtsproHomepage}` } });
    }

    if (userResult.status === 'fulfilled') {
      user = rtsUser.status === 'fulfilled' ? { ...rtsUser.value.user, ...userResult.value } : userResult.value;
    } else if (rtsUser.status === 'fulfilled') {
      user = rtsUser.value.user;
    } else {
      if ([401, 403].includes(userResult.reason.response.status)) {
        setLoading(false);
        isAuthenticatedAuth0 ? redirectToV2Logout() : redirectToLogout();
        return;
      }

      setLoading(false);
      setError('user');
      return;
    }
    await attemptSetIMUser(user, email, dispatch);

    dispatch(setUser(user));
    dispatch(setFeatures(features));
    setLoading(false);
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && features?.length && !user && !loading) loadData();
  }, [isAuthenticated, features, user]);

  return { error, loading: loading || isLoading };
}
